import { supabase } from '@/core/database/supabase';
import type { 
  UtilitySystem, 
  UtilityComponent, 
  WorkOrderUtilityLink,
  UtilityStats
} from '../types/utility.types';

export const UtilitySystemsService = {
  /**
   * Danh sách hệ thống hạ tầng
   */
  async getSystems(companyId: string, projectId?: string): Promise<UtilitySystem[]> {
    let query = supabase
      .from('utility_assets')
      .select(`
        id,
        company_id,
        system_code:code,
        system_name:name,
        status_code,
        description,
        created_at,
        updated_at,
        group:utility_groups(id, name)
      `)
      .eq('company_id', companyId);

    // Filter by project_id is ignored for now

    const { data, error } = await query.order('created_at', { ascending: false });

    if (error) throw error;
    return (data as any[]).map(d => ({
      ...d,
      system_type: d.group?.name || 'Tiện ích',
      project_id: '',
      zone_id: ''
    })) as UtilitySystem[];
  },

  /**
   * Chi tiết hệ thống
   */
  async getSystemDetail(id: string): Promise<UtilitySystem> {
    const { data, error } = await supabase
      .from('utility_assets')
      .select(`
        id,
        company_id,
        system_code:code,
        system_name:name,
        status_code,
        description,
        created_at,
        updated_at,
        group:utility_groups(id, name)
      `)
      .eq('id', id)
      .single();

    if (error) throw error;
    return {
      ...data,
      system_type: data.group?.name || 'Tiện ích',
      project_id: '',
      zone_id: ''
    } as any as UtilitySystem;
  },

  /**
   * Danh sách linh kiện trong hệ thống
   */
  async getComponents(systemId: string): Promise<UtilityComponent[]> {
    const { data, error } = await supabase
      .from('utility_components')
      .select('*')
      .eq('system_id', systemId)
      .order('created_at', { ascending: true });

    if (error) throw error;
    return data as UtilityComponent[];
  },

  /**
   * Thống kê hạ tầng
   */
  async getStats(companyId: string, projectId?: string): Promise<UtilityStats> {
    let query = supabase.from('utility_assets').select('status_code').eq('company_id', companyId);

    const { data, error } = await query;
    if (error) throw error;

    return {
      totalSystems: data.length,
      installingCount: data.filter(s => s.status_code === 'installing').length,
      testingCount: data.filter(s => s.status_code === 'testing').length,
      operationalCount: data.filter(s => s.status_code === 'operational' || s.status_code === 'active').length,
      activeCount: data.filter(s => s.status_code === 'active' || s.status_code === 'operational').length,
      faultyCount: data.filter(s => s.status_code === 'faulty').length,
      maintenanceDueCount: data.filter(s => s.status_code === 'maintenance_due').length,
      totalComponents: 0, // Should be fetched separately or calculated
    };
  },

  /**
   * Lấy danh mục utility liên kết với Work Order
   */
  async getWorkOrderUtilities(workOrderId: string): Promise<WorkOrderUtilityLink[]> {
    const { data, error } = await supabase
      .from('construction_work_order_utility')
      .select(`
        *,
        system:utility_assets(id, system_name:name, system_code:code),
        component:utility_components(id, name, code)
      `)
      .eq('work_order_id', workOrderId);

    if (error) throw error;
    return data as WorkOrderUtilityLink[];
  },

  /**
   * Ghi nhận khối lượng lắp đặt
   */
  async logInstallation(linkId: string, installedQty: number): Promise<void> {
    const { error } = await supabase
      .from('construction_work_order_utility')
      .update({ 
        installed_quantity: installedQty,
        status_code: 'installed'
      })
      .eq('id', linkId);

    if (error) throw error;
  },

  /**
   * Tạo hệ thống mới
   */
  async createSystem(system: Partial<UtilitySystem>): Promise<UtilitySystem> {
    const { data, error } = await supabase
      .from('utility_assets')
      .insert([{
        name: system.system_name,
        code: system.system_code,
        status_code: system.status_code,
        description: system.description,
        company_id: system.company_id,
        group_id: system.group?.id
      }])
      .select()
      .single();

    if (error) throw error;
    return data as any as UtilitySystem;
  },

  /**
   * Cập nhật hệ thống
   */
  async updateSystem(id: string, updates: Partial<UtilitySystem>): Promise<UtilitySystem> {
    const { data, error } = await supabase
      .from('utility_assets')
      .update({
        name: updates.system_name,
        code: updates.system_code,
        status_code: updates.status_code,
        description: updates.description,
        group_id: updates.group?.id
      })
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return data as any as UtilitySystem;
  }
};
